import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { fmtDate, fmtMoney } from '../../lib/format';
import { PHASE_TYPE_LABEL } from '../phases/types';
import type { QueueItem } from './api';

// Ranní fronta: konkrétní věci, které je potřeba dnes vyřešit, seskupené
// podle toho, co s nimi dělat. Každý řádek vede rovnou na místo, kde se to řeší.

type Tone = 'red' | 'amber' | 'blue';
interface Row { key: string; tone: Tone; title: string; detail?: string; to: string; search?: Record<string, string>; action: string }

const SECTIONS: Array<{ id: string; title: string; kinds: QueueItem['kind'][] }> = [
  { id: 'blok', title: 'Blokuje výrobu', kinds: ['blocked'] },
  { id: 'termin', title: 'Po termínu a ohrožené termíny', kinds: ['overdue_phase', 'deadline'] },
  { id: 'tebe', title: 'Čeká na tebe', kinds: ['client_decision', 'assign_quote', 'overdue_invoice', 'send_invoice', 'inquiry', 'handover'] },
  { id: 'chybi', title: 'Bude chybět', kinds: ['low_stock'] },
  { id: 'rozpocet', title: 'Rozpočet', kinds: ['budget'] },
];

const days = (n: number) => (n === 1 ? '1 den' : n >= 2 && n <= 4 ? `${n} dny` : `${n} dní`);

function toRow(it: QueueItem): Row {
  switch (it.kind) {
    case 'blocked':
      return { key: it.id, tone: 'red', title: `${it.propItemName} — ${PHASE_TYPE_LABEL[it.phaseType]}`, detail: `${it.projectCode}${it.reason ? ` · ${it.reason}` : ''}`, to: `/vyroby/${it.propItemId}`, action: 'Odblokovat' };
    case 'overdue_phase':
      return { key: it.id, tone: 'red', title: `${it.propItemName} — ${PHASE_TYPE_LABEL[it.phaseType]}`, detail: `${it.projectCode} · ${days(it.daysLate)} po termínu${it.assignee ? ` · ${it.assignee}` : ''}`, to: `/vyroby/${it.propItemId}`, action: 'Otevřít' };
    case 'deadline':
      return {
        key: it.id, tone: it.daysUntil < 0 ? 'red' : 'amber', title: `${it.code} · ${it.name}`,
        detail: `${it.daysUntil < 0 ? `termín prošel před ${days(-it.daysUntil)}` : it.daysUntil === 0 ? 'termín dnes' : `termín za ${days(it.daysUntil)} (${fmtDate(it.deadline)})`} · ${it.openPhases} otevřených fází`,
        to: '/zakazky', search: { pohled: 'harmonogram' }, action: 'Harmonogram',
      };
    case 'handover':
      return { key: it.id, tone: 'blue', title: `${it.code} · ${it.name}`, detail: `${it.count === 1 ? '1 dokončená výroba čeká' : `${it.count} dokončené výroby čekají`} na předání klientovi`, to: `/zakazky/${it.id}`, action: 'Předat' };
    case 'low_stock':
      return {
        key: it.id, tone: 'amber', title: it.name,
        detail: it.reserved > 0
          ? `skladem ${it.stock} ${it.unit}, z toho ${it.reserved} rezervováno · minimum ${it.min} ${it.unit}`
          : `skladem ${it.stock} ${it.unit}, minimum ${it.min} ${it.unit}`,
        to: '/sklad', action: 'Sklad',
      };
    case 'client_decision':
      return { key: it.id, tone: it.accepted ? 'blue' : 'amber', title: `Klient ${it.accepted ? 'schválil' : 'odmítl'} nabídku ${it.title ? `„${it.title}"` : `#${it.version}`}`, detail: `${it.code}${it.decidedBy ? ` · ${it.decidedBy}` : ''}`, to: `/zakazky/${it.projectId}`, action: 'Otevřít' };
    case 'assign_quote':
      return { key: it.id, tone: 'blue', title: `Přiřaď nabídku ${it.title ? `„${it.title}"` : `#${it.version}`} k výrobám`, detail: `${it.code} · přijatá, zatím bez výroby`, to: `/zakazky/${it.projectId}`, action: 'Přiřadit' };
    case 'overdue_invoice':
      return { key: it.id, tone: 'red', title: `Faktura ${it.number} po splatnosti`, detail: `${it.label} · ${days(it.daysOverdue)} · ${fmtMoney(it.total, it.currency)}`, to: it.link, action: 'Upomenout' };
    case 'send_invoice':
      return { key: it.id, tone: 'blue', title: `Odeslat fakturu ${it.number}`, detail: it.label, to: it.link, action: 'Odeslat' };
    case 'inquiry':
      return { key: it.id, tone: 'blue', title: `Poptávka půjčovny — ${it.customerName}`, detail: `přišla ${fmtDate(it.createdAt)}`, to: '/pujcovna/poptavky', action: 'Vyřídit' };
    case 'budget':
      return { key: it.id, tone: 'red', title: `${it.code} · ${it.name}`, detail: `náklad ${fmtMoney(it.actual, it.currency)} z objednaných ${fmtMoney(it.ordered, it.currency)} (${Math.round((it.actual / it.ordered) * 100)} %)`, to: `/zakazky/${it.id}`, action: 'Otevřít' };
  }
}

const DOT: Record<Tone, string> = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  blue: 'bg-sky-500',
};

const SHOWN = 3;

export default function MorningQueue({ items }: { items: QueueItem[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const groups = SECTIONS.map((s) => ({ ...s, rows: items.filter((i) => s.kinds.includes(i.kind)).map(toRow) })).filter((g) => g.rows.length > 0);

  if (groups.length === 0) {
    return (
      <section className="flex items-center gap-3 rounded-xl border border-emerald-200/70 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-950/15 px-5 py-4">
        <CheckCircle2 size={22} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div>
          <h2 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Vše pod kontrolou</h2>
          <p className="text-sm text-emerald-700/80 dark:text-emerald-400/70">Nic neblokuje, nic není po termínu a nic nečeká na tebe.</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-400">Co dnes vyřešit</h2>
      <div className="grid gap-3 lg:grid-cols-2">
        {groups.map((g) => {
          const expanded = open[g.id];
          const rows = expanded ? g.rows : g.rows.slice(0, SHOWN);
          return (
            <div key={g.id} className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
              <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5 dark:border-zinc-800">
                <h3 className="text-sm font-semibold">{g.title}</h3>
                <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">{g.rows.length}</span>
              </div>
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {rows.map((r) => (
                  <li key={r.key} className="flex items-center gap-3 px-4 py-2.5">
                    <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${DOT[r.tone]}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{r.title}</div>
                      {r.detail && <div className="truncate text-xs text-zinc-500">{r.detail}</div>}
                    </div>
                    <Link
                      to={r.to}
                      search={r.search as never}
                      className="shrink-0 rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-medium hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                    >
                      {r.action}
                    </Link>
                  </li>
                ))}
              </ul>
              {g.rows.length > SHOWN && (
                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}
                  className="w-full border-t border-zinc-100 px-4 py-2 text-left text-xs text-zinc-500 hover:text-zinc-800 dark:border-zinc-800 dark:hover:text-zinc-200"
                >
                  {expanded ? 'Zobrazit méně' : `Zobrazit další (${g.rows.length - SHOWN})`}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
